import extractDomain from 'extract-domain';
import Cookies from 'js-cookie';

const KEY = 'affiliation';
const EXPIRY_DAYS = 90;

const cookieDomain = window.location.hostname.includes('.') ? '.' + extractDomain(window.location.hostname) : window.location.hostname;

export const retrieveParams = () => {
  const currentParams = extractParams();

  if (currentParams) {
    saveParams(currentParams);
    return currentParams;
  }

  return retrieveSavedParams();
};

export const getFacebookBrowserId = () => {
  try {
    console.log('=== getFacebookBrowserId DEBUG START ===');
    
    if (typeof document === 'undefined') {
      console.log('FBID: document undefined');
      return null;
    }
    
    // Log all available cookies
    console.log('FBID: Raw document.cookie =', document.cookie);
    
    // ALWAYS try document.cookie FIRST (most reliable)
    console.log('FBID: Attempting to read directly from document.cookie');
    const cookieArray = document.cookie.split(';');
    console.log('FBID: Cookie array length:', cookieArray.length);
    
    const fbpFromDocument = cookieArray.find(c => c.trim().startsWith('_fbp='));
    console.log('FBID: Found _fbp in document.cookie:', fbpFromDocument);
    
    if (fbpFromDocument) {
      const value = fbpFromDocument.split('=')[1].trim();
      console.log('FBID: SUCCESS (document.cookie) - Extracted value:', value);
      console.log('=== getFacebookBrowserId DEBUG END ===');
      return value;
    }
    
    // Fallback to js-cookie library (might have domain/path issues)
    if (typeof Cookies !== 'undefined') {
      console.log('FBID: document.cookie failed, trying Cookies.get()');
      console.log('FBID: All cookies via Cookies.get() =', Cookies.get());
      
      const fbpCookie = Cookies.get('_fbp');
      console.log('FBID: Cookies.get("_fbp") returned =', fbpCookie);
      
      if (fbpCookie) {
        console.log('FBID: SUCCESS (Cookies.get) - Returning value:', fbpCookie);
        console.log('=== getFacebookBrowserId DEBUG END ===');
        return fbpCookie;
      }
    } else {
      console.log('FBID: Cookies library not available');
    }
    
    // Fallback to query string parameter
    if (typeof window !== 'undefined' && window.location.search) {
      console.log('FBID: Cookies failed, checking URL params');
      const params = new URLSearchParams(window.location.search);
      const fbpParam = params.get('_fbp');
      console.log('FBID: URL parameter _fbp =', fbpParam);
      
      if (fbpParam) {
        console.log('FBID: SUCCESS (URL param) - Returning value:', fbpParam);
        console.log('=== getFacebookBrowserId DEBUG END ===');
        return fbpParam;
      }
    }
    
    console.log('FBID: FAILED - No _fbp cookie or URL parameter found');
    console.log('=== getFacebookBrowserId DEBUG END ===');
    return null;
  } catch (error) {
    console.error('FBID: ERROR - Exception thrown:', error);
    console.log('=== getFacebookBrowserId DEBUG END ===');
    return null;
  }
};

/**
 * Extract Facebook Click ID (fbclid) from _fbc cookie or URL parameter
 * _fbc cookie format: fb.1.{timestamp}.{fbclid}
 * Priority: _fbc cookie → fbclid URL parameter
 * @returns fbclid string or null if not found
 */
export const getFacebookClickId = () => {
  try {
    if (typeof document === 'undefined') return null;
    
    // Try to get from _fbc cookie - read directly from document.cookie first
    const cookieArray = document.cookie.split(';');
    const fbcFromDocument = cookieArray.find(c => c.trim().startsWith('_fbc='));
    
    if (fbcFromDocument) {
      const fbcCookie = fbcFromDocument.split('=')[1].trim();
      // Parse _fbc cookie format: fb.1.{timestamp}.{fbclid}
      const parts = fbcCookie.split('.');
      if (parts.length >= 4) {
        const fbclid = parts.slice(3).join('.'); // Join in case fbclid contains dots
        return fbclid;
      }
    }
    
    // Fallback to js-cookie library
    if (typeof Cookies !== 'undefined') {
      const fbcCookie = Cookies.get('_fbc');
      if (fbcCookie) {
        const parts = fbcCookie.split('.');
        if (parts.length >= 4) {
          const fbclid = parts.slice(3).join('.');
          return fbclid;
        }
      }
    }
    
    // Fallback to query string parameter
    if (typeof window !== 'undefined' && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      const fbclidParam = params.get('fbclid');
      if (fbclidParam) return fbclidParam;
    }
    
    return null;
  } catch (error) {
    console.warn('Error reading Facebook Click ID:', error);
    return null;
  }
};

/**
 * Extract GA4 Client ID from the main _ga cookie or query string
 * @returns Client ID string or null if not found
 */
export function getGA4ClientId() {
  try {
    if (typeof document === 'undefined') return null;
    
    // First try to get from cookie
    if (document.cookie) {
      const match = document.cookie.match(/_ga=GA1\.\d+\.(.+?)(?:;|$)/);
      if (match && match[1]) return match[1];
    }
    
    // Fallback to query string parameter
    if (typeof window !== 'undefined' && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      const gaParam = params.get('_ga');
      if (gaParam) {
        // Parse format: GA1.2.123456789 to extract 123456789
        const gaMatch = gaParam.match(/^GA1\.\d+\.(.+)$/);
        if (gaMatch && gaMatch[1]) return gaMatch[1];
      }
    }
    
    return null;
  } catch (error) {
    console.warn('Error reading GA4 Client ID:', error);
    return null;
  }
}

/**
 * Extract GA4 Session data from GA4-specific cookies (_ga_MEASUREMENT_ID)
 * Supports both GS1 and GS2 formats
 * @returns Object with sessionId and sessionNumber, or null if not found
 */
export function getGA4SessionData() {
  try {
    if (typeof document === 'undefined') return null;
    if (!document.cookie) return null;
    
    const cookies = document.cookie.split(';');
    
    // Look for GA4 cookies that match the pattern _ga_XXXXXXXXXX
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      const ga4Match = trimmed.match(/^_ga_([A-Z0-9]+)=(.+)$/);
      
      if (ga4Match) {
        const cookieValue = ga4Match[2];
        
        // Handle GS2 format (newer format with prefixes)
        if (cookieValue.startsWith('GS2.1.')) {
          const data = cookieValue.substring(6); // Remove "GS2.1."
          const segments = data.replace(/%24/g, '$').split('$');
          
          let sessionId = '';
          let sessionNumber = '';
          
          for (const segment of segments) {
            if (segment.startsWith('s')) {
              sessionId = segment.substring(1);
            } else if (segment.startsWith('o')) {
              sessionNumber = segment.substring(1);
            }
          }
          
          if (sessionId && sessionNumber) {
            return { sessionId, sessionNumber };
          }
        }
        
        // Handle GS1 format (older format with dot separation)
        else if (cookieValue.startsWith('GS1.1.')) {
          const parts = cookieValue.split('.');
          if (parts.length >= 4) {
            return {
              sessionId: parts[2],
              sessionNumber: parts[3]
            };
          }
        }
      }
    }
    
    return null;
  } catch (error) {
    console.warn('Error reading GA4 Session data:', error);
    return null;
  }
}

const extractParams = () => {
  const params = new URLSearchParams(window.location.search);
  const trackingParams = {
    source: (params.get('utm_source') || '').toLowerCase(),
    code: null,
    query: window.location.search,
  };

  if (params.get('gclid')) {
    trackingParams.source = 'google';
    trackingParams.code = params.get('gclid');
    
    // For Google: Use GA4 Session ID as browserId (with error handling)
    try {
      const ga4SessionData = getGA4SessionData();
      if (ga4SessionData && ga4SessionData.sessionId) {
        trackingParams.browserId = ga4SessionData.sessionId;
      }
    } catch (error) {
      console.warn('Error extracting Google GA4 Session ID:', error);
    }

    return trackingParams;
  }

  // Check for Facebook Click ID from _fbc cookie first, then URL parameter
  const fbclid = getFacebookClickId();
  if (fbclid) {
    trackingParams.source = 'facebook';
    trackingParams.code = fbclid;
    
    // For Facebook: Use _fbp cookie as browserId (with error handling)
    try {
      const fbpCookie = getFacebookBrowserId();
      if (fbpCookie) {
        trackingParams.browserId = fbpCookie;
      }
    } catch (error) {
      console.warn('Error extracting Facebook _fbp cookie:', error);
    }
    
    // Add GA4 tracking data (with error handling)
    try {
      const ga4ClientId = getGA4ClientId();
      const ga4SessionData = getGA4SessionData();
      
      if (ga4ClientId) {
        trackingParams.ga4ClientId = ga4ClientId;
      }
      if (ga4SessionData && ga4SessionData.sessionId) {
        trackingParams.ga4SessionId = ga4SessionData.sessionId;
      }
    } catch (error) {
      console.warn('Error extracting GA4 data for Facebook:', error);
    }

    return trackingParams;
  }

  switch (trackingParams.source) {
    case 'affiliate':
      trackingParams.source = 'adservice';
      trackingParams.code = params.get('coid');
      return trackingParams;
    case 'adtraction':
      trackingParams.code = params.get('at_gd');
      return trackingParams;
    case 'ilona':
      trackingParams.code = params.get('utm_campaign');
      return trackingParams;
    default:
      return null;
  }
};

const saveParams = (value) => {
  Cookies.set(KEY, JSON.stringify(value), { expires: EXPIRY_DAYS, path: '/', domain: cookieDomain });
};

const retrieveSavedParams = () => {
  if (Cookies.get(KEY)) {
    return JSON.parse(Cookies.get(KEY));
  }
  return null;
};